'use client';

import { useState, useEffect, useCallback } from 'react';
import { MarketingHeader, MarketingFooter } from '@/components/marketing';
import { BRAND } from '@/lib/brand';

interface HealthCheck {
  status: 'ok' | 'error';
  message?: string;
  latency?: number;
}

interface HealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  checks: {
    application: HealthCheck;
    firebase: HealthCheck;
    environment: HealthCheck;
  };
  version?: string;
}

const SERVICE_LABELS: Record<string, string> = {
  application: 'Application',
  firebase: 'Firebase',
  environment: 'Environment',
};

function StatusDot({ status }: { status: 'ok' | 'error' }) {
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${
        status === 'ok' ? 'bg-green-500' : 'bg-red-500'
      }`}
    />
  );
}

function OverallDot({ status }: { status: HealthResponse['status'] }) {
  const color =
    status === 'healthy'
      ? 'bg-green-500'
      : status === 'degraded'
        ? 'bg-amber-500'
        : 'bg-red-500';

  return <span className={`inline-block w-3 h-3 rounded-full ${color}`} />;
}

function overallLabel(status: HealthResponse['status']) {
  switch (status) {
    case 'healthy':
      return 'All systems operational';
    case 'degraded':
      return 'Partial degradation';
    default:
      return 'Service disruption';
  }
}

export default function StatusPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState(false);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch('/api/health', { cache: 'no-store' });
      const data: HealthResponse = await res.json();
      setHealth(data);
      setError(false);
      setLastFetched(new Date());
      setSecondsAgo(0);
    } catch {
      setError(true);
    }
  }, []);

  // Initial fetch + auto-refresh every 30s
  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  // Update "seconds ago" counter every second
  useEffect(() => {
    const tick = setInterval(() => {
      if (lastFetched) {
        setSecondsAgo(Math.floor((Date.now() - lastFetched.getTime()) / 1000));
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [lastFetched]);

  const timeAgoText =
    secondsAgo < 5
      ? 'just now'
      : secondsAgo < 60
        ? `${secondsAgo}s ago`
        : `${Math.floor(secondsAgo / 60)}m ago`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <MarketingHeader variant="light" />

      <main className="flex-1 px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              System Status
            </h1>

            {error && !health ? (
              <div className="flex items-center gap-2.5">
                <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
                <span className="text-lg text-red-700 font-medium">
                  Unable to reach health endpoint
                </span>
              </div>
            ) : health ? (
              <>
                <div className="flex items-center gap-2.5">
                  <OverallDot status={health.status} />
                  <span className="text-lg text-gray-700 font-medium">
                    {overallLabel(health.status)}
                  </span>
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  Updated {timeAgoText}
                  {health.version && health.version !== 'unknown' && (
                    <span className="ml-2 text-gray-300">
                      · v{health.version}
                    </span>
                  )}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-400">Checking...</p>
            )}
          </div>

          {/* Service rows */}
          {health && (
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-200">
              {(Object.entries(health.checks) as [string, HealthCheck][]).map(
                ([key, check]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between px-5 py-4"
                  >
                    <div className="flex items-center gap-3">
                      <StatusDot status={check.status} />
                      <span className="font-medium text-gray-900">
                        {SERVICE_LABELS[key] || key}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {check.status === 'ok' ? (
                        check.latency != null ? (
                          <span>{check.latency}ms</span>
                        ) : check.message ? (
                          <span>{check.message}</span>
                        ) : (
                          <span>OK</span>
                        )
                      ) : (
                        <span className="text-red-600">
                          {check.message || 'Error'}
                        </span>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 mt-6">
            Auto-refreshes every 30 seconds
          </p>
        </div>
      </main>

      <MarketingFooter variant="light" />
    </div>
  );
}
